import { useGetButtons, useGetTopMenus } from '@hts/features/libs';
import { getStaticElements } from '@hts/shared/api';
import { useGetPageDepartment, useShouldRenderOnCurrentView } from '@hts/shared/libs';
import {
  IMappedButton,
  IMappedSubmenuCategory,
  IPageDepartmentSectionItem,
} from '@hts/shared/types';
import {
  HtsButton,
  HtsButtonIcon,
  HtsLink,
  HtsTypography,
  HtslIcon,
  LinkElement,
} from '@hts/shared/ui';
import { SwipeableDrawer } from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from '../../../styles/Header/Mobile/MobileMenu.module.scss';
import { AllSeasonsMenu } from './AllSeasonsMenu';
import { AllSimchasMenu } from './AllSimchasMenu';
import { ExpandedCategory } from './ExpandedCategory';

interface MobileMenuProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export const MobileMenu: React.FC<MobileMenuProps> = memo(({ isOpen, onClose, onOpen }) => {
  const { data } = getStaticElements.useQueryState({ link: '/' });
  const { pageDepartment } = useGetPageDepartment();

  const shouldRenderPageDepartment = useShouldRenderOnCurrentView(
    pageDepartment?.shouldShowOnDesktop,
    pageDepartment?.shouldShowOnMobile
  );

  const { topMenu2 } = useGetTopMenus(data?.menus);
  const buttons = useGetButtons(data?.buttons);

  const topMenus2Categories = topMenu2?.submenus?.[0]?.categories ?? [];

  const [isOpenAllSimchas, setOpenAllSimchas] = useState(false);
  const [isOpenAllSeasons, setOpenAllSeasons] = useState(false);

  const onOpenAllSimchas = useCallback(() => setOpenAllSimchas(true), []);
  const onCloseAllSimchas = useCallback(() => setOpenAllSimchas(false), []);

  const onOpenAllSeasons = useCallback(() => setOpenAllSeasons(true), []);
  const onCloseAllSeasons = useCallback(() => setOpenAllSeasons(false), []);

  const renderTop2MenuSubmenu = useCallback(
    (category: IMappedSubmenuCategory, index: number) => (
      <ExpandedCategory key={`${category.id}-${index}`} category={category} onClose={onClose} />
    ),
    [onClose]
  );

  const renderButton = useCallback(
    ({ id, buttonText, buttonColor, link, title }: IMappedButton, index: number) => (
      <Link key={`${id}-${index}`} to={link} onClick={onClose}>
        <HtsButton
          label={buttonText}
          className={styles.entryButton}
          style={{ background: buttonColor }}
        />
      </Link>
    ),
    [onClose]
  );

  const renderSite = useCallback(
    ({ id, link, title }: IPageDepartmentSectionItem, index: number) => (
      <li key={`${id}-${index}`}>
        <HtsLink
          label={title}
          redirectTo={link}
          typographyProps={{ variant: 'body2', fontWeight: '400' }}
          linkProps={{ onClick: onClose }}
        />
      </li>
    ),

    [onClose]
  );

  return (
    <>
      <SwipeableDrawer
        anchor="left"
        open={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        SlideProps={{ className: styles.slideContainer }}
      >
        <div className={styles.container}>
          <div className={styles.contentContainer}>
            <div className={styles.closeContainer}>
              <HtsButtonIcon onClick={onClose}>
                <HtslIcon icon="cancel" />
              </HtsButtonIcon>
            </div>

            <div className={styles.topContentContainer}>
              <HtsButton label="All Simchas" variant="text" onClick={onOpenAllSimchas} />

              <HtsButton label="All Seasons" variant="text" onClick={onOpenAllSeasons} />
            </div>

            {Boolean(buttons.length) && (
              <div className={styles.buttonsContainer}>{buttons.map(renderButton)}</div>
            )}

            {Boolean(topMenus2Categories.length) && (
              <ul className={styles.categoriesContainer}>
                {topMenus2Categories.map(renderTop2MenuSubmenu)}
              </ul>
            )}

            <div className={styles.linksContainer}>
              <LinkElement type="help" onCloseCb={onClose} />
              <LinkElement type="faq" onCloseCb={onClose} />
              {/* <LinkElement type="signInUp" /> */}
            </div>
          </div>

          {shouldRenderPageDepartment && (
            <div className={styles.bottomContainer}>
              <HtsTypography variant="caption">Our Sites</HtsTypography>

              <ul>{pageDepartment?.sectionItems?.map(renderSite)}</ul>
            </div>
          )}
        </div>
      </SwipeableDrawer>

      <AllSimchasMenu
        isOpen={isOpenAllSimchas}
        staticElements={data}
        onOpen={onOpenAllSimchas}
        onClose={onCloseAllSimchas}
        onCloseDrawer={onClose}
      />

      <AllSeasonsMenu
        isOpen={isOpenAllSeasons}
        staticElements={data}
        onOpen={onOpenAllSeasons}
        onClose={onCloseAllSeasons}
        onCloseDrawer={onClose}
      />
    </>
  );
});
